// src/pages/ja/MainPage_ja.js
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import SupportPopup from "../../components/SupportPopup";
import SupportSection from "../../components/SupportSection";
import "../MainPage.css";

function MainPage_ja() {
  const location = useLocation();
  const pathname = location?.pathname || "/";
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const {
    currentLocale,
    prefix,
    canonicalUrl,
    htmlLang,
    ogLocale,
    inLanguage,
    altPaths,
  } = useMemo(() => {
    const firstSeg = pathname.split("/").filter(Boolean)[0];
    const supported = ["en", "ja", "tw"];
    const locale = supported.includes(firstSeg) ? firstSeg : "ko";
    const pref = locale === "ko" ? "" : `/${locale}`;

    const segments = pathname.split("/").filter(Boolean);
    const suffixSegments = supported.includes(segments[0])
      ? segments.slice(1)
      : segments;
    const suffixPath = "/" + suffixSegments.join("/");
    const normalizedSuffix = suffixPath === "//" ? "/" : suffixPath;

    const alt = {
      ko: normalizedSuffix,
      en: "/en" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
      ja: "/ja" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
      tw: "/tw" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
    };

    const htmlLangMap = { ko: "ko", en: "en", ja: "ja", tw: "zh-TW" };
    const ogLocaleMap = { ko: "ko_KR", en: "en_US", ja: "ja_JP", tw: "zh_TW" };
    const inLangMap = { ko: "ko-KR", en: "en", ja: "ja", tw: "zh-TW" };

    return {
      currentLocale: locale,
      prefix: pref,
      canonicalUrl: (origin || "") + pathname,
      htmlLang: htmlLangMap[locale],
      ogLocale: ogLocaleMap[locale],
      inLanguage: inLangMap[locale],
      altPaths: alt,
    };
  }, [pathname, origin]);

  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "TooLoves",
      description: "よく使うツールをひとつに、すばやく。",
      inLanguage,
      potentialAction: {
        "@type": "SearchAction",
        target: `${origin}/?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl, inLanguage, origin]);

  const alternateLinks =
    origin &&
    [
      <link key="ko" rel="alternate" hrefLang="ko" href={origin + altPaths.ko} />,
      <link key="en" rel="alternate" hrefLang="en" href={origin + altPaths.en} />,
      <link key="ja" rel="alternate" hrefLang="ja" href={origin + altPaths.ja} />,
      <link
        key="tw"
        rel="alternate"
        hrefLang="zh-TW"
        href={origin + altPaths.tw}
      />,
      <link
        key="xdef"
        rel="alternate"
        hrefLang="x-default"
        href={origin + altPaths.ko}
      />,
    ];

  return (
    <div className="main-wrap">
      <SupportPopup locale="ja" />
      <Helmet htmlAttributes={{ lang: htmlLang }}>
        <title>TooLoves</title>
        <meta name="description" content="よく使うツールをひとつに、すばやく。" />
        <meta
          name="keywords"
          content="TooLoves, ツール集, QRコード, Base64, 透かし, ウォーターマーク, EXIF, メタデータ抽出, AI画像, 無料, Webユーティリティ, ブログ, Web開発"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#ffffff" />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="TooLoves" />
        <meta property="og:description" content="よく使うツールをひとつに、すばやく。" />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-main.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TooLoves" />
        <meta name="twitter:description" content="よく使うツールをひとつに、すばやく。" />
        <meta name="twitter:image" content="/og-main.png" />

        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {alternateLinks}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLd }}
        />
        <style>
          {
            `.lang-switcher {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 14px;
            }
            .lang-switcher .lang-btn {
              color: inherit;
              opacity: 0.7;
              text-decoration: none;
              padding: 4px 8px;
              border-radius: 8px;
              transition: opacity .15s, background-color .15s;
            }
            .lang-switcher .lang-btn:hover {
              opacity: 1;
              background: rgba(0,0,0,0.06);
            }
            .lang-switcher .lang-btn.active {
              opacity: 1;
              font-weight: 600;
              background: rgba(0,0,0,0.08);
            }
            .lang-switcher .lang-sep {
              opacity: 0.4;
              user-select: none;
            }
            @media (prefers-color-scheme: dark) {
              .lang-switcher .lang-btn:hover {
                background: rgba(255,255,255,0.08);
              }
              .lang-switcher .lang-btn.active {
                background: rgba(255,255,255,0.12);
              }
            }`
          }
        </style>
      </Helmet>

      <header className="main-hero">
        <nav className="main-nav">
          <div className="nav-left"></div>
          <div className="nav-right">
            <div className="lang-switcher" role="group" aria-label="言語選択">
              {["ko", "en", "ja", "tw"].map((lang, i, arr) => (
                <React.Fragment key={lang}>
                  <Link
                    to={altPaths[lang]}
                    className={`lang-btn ${currentLocale === lang ? "active" : ""}`}
                    lang={lang === "tw" ? "zh-TW" : lang}
                    aria-current={currentLocale === lang ? "page" : undefined}
                  >
                    {lang === "ko"
                      ? "한국어"
                      : lang === "en"
                      ? "English"
                      : lang === "ja"
                      ? "日本語"
                      : "繁體中文"}
                  </Link>
                  {i < arr.length - 1 && (
                    <span className="lang-sep" aria-hidden="true">
                      /
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </nav>

        <div className="hero-content">
          <h1 className="hero-title">TooLoves</h1>
          <p className="hero-subtitle">よく使うツールをひとつに、すばやく。</p>
        </div>
      </header>

      <main className="services">
        <SupportSection locale="ja" />
        <h2 className="section-title">サービス一覧</h2>
        <div className="service-grid">
          {/* QRコード作成 */}
          <article className="service-card">
            <div className="service-icon service-icon-qr" aria-hidden="true">
              <svg viewBox="0 0 64 64" width="48" height="48" role="img">
                <rect x="6" y="6" width="18" height="18" rx="2" />
                <rect x="40" y="6" width="18" height="18" rx="2" />
                <rect x="6" y="40" width="18" height="18" rx="2" />
                <rect x="26" y="26" width="12" height="12" />
              </svg>
            </div>
            <h3 className="service-name">QRコード</h3>
            <p className="service-desc">URLをQRコードにして画像として保存</p>
            <Link to={`${prefix}/qr`} className="btn-primary">
              QR作成ツールへ
            </Link>
          </article>

          {/* 透かし(ウォーターマーク) */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🖼️</div>
            <h3 className="service-name">画像に透かしを追加</h3>
            <p className="service-desc">あなたのロゴや文字を画像に追加します</p>
            <Link to={`${prefix}/watermark`} className="btn-primary">
              透かし作成ツールへ
            </Link>
          </article>

          {/* AI画像EXIF */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🌀</div>
            <h3 className="service-name">NovelAI画像 EXIF抽出</h3>
            <p className="service-desc">AI画像のメタデータを簡単に確認</p>
            <Link to={`${prefix}/aiexif`} className="btn-primary">
              EXIF抽出ツールへ
            </Link>
          </article>

          {/* Base64変換 */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🔤</div>
            <h3 className="service-name">Base64エンコード / デコード</h3>
            <p className="service-desc">テキストをBase64形式に変換または復号</p>
            <Link to={`${prefix}/base64`} className="btn-primary">
              Base64変換ツールへ
            </Link>
          </article>
        </div>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="法的リンク">
          <Link to={`${prefix}/terms`} className="footer-link">
            利用規約
          </Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <Link to={`${prefix}/privacy`} className="footer-link">
            プライバシーポリシー
          </Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <a
            href="https://tooloves.net/blog"
            className="footer-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            ブログ
          </a>
        </nav>
      </footer>
    </div>
  );
}

export default MainPage_ja;
